<h2 class="mat-h2">Season overview</h2>

<mat-card class="card" *ngIf="summaries.length">
  <mat-card-header>
    <mat-card-title>Where the work is</mat-card-title>
    <mat-card-subtitle>Open items are spread unevenly, so clear one production at a time.</mat-card-subtitle>
  </mat-card-header>
  <mat-card-content>
    <table class="summary">
      <thead>
        <tr>
          <th>Producer</th>
          <th>Broadcasts</th>
          <th>Open items</th>
          <th matTooltip="Settled and still ours — what the button below would send">Ready to release</th>
          <th matTooltip="Out with the production company, waiting for them">Released</th>
          <th matTooltip="Sent back with a note — our turn again">Returned</th>
          <th>Approved</th>
          <th matTooltip="Claims worth 825 or 1650 that nobody has put their name to yet. A declaration of none is not one — there is nothing to confirm.">Camera to confirm</th>
          <th matTooltip="An admin does not accept the declaration; the amount stands until it is settled">Camera disputed</th>
          <th>Approved total</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let summary of summaries" (click)="filterTo(summary.name)" class="clickable">
          <td>{{ summary.name }}</td>
          <td>{{ summary.games }}</td>
          <td [ngClass]="summary.pending ? 'warn' : 'muted'">{{ summary.pending }}</td>
          <td>{{ summary.toRelease }}</td>
          <td [ngClass]="summary.released ? '' : 'muted'">{{ summary.released || '—' }}</td>
          <!-- A returned broadcast is somebody waiting on an answer, so it
               reads as work rather than as a count. -->
          <td [ngClass]="summary.returned ? 'warn' : 'muted'">{{ summary.returned || '—' }}</td>
          <td>{{ summary.approved }}</td>
          <td [ngClass]="summary.toConfirm ? 'warn' : 'muted'">{{ summary.toConfirm || '—' }}</td>
          <td [ngClass]="summary.disputed ? 'bad' : 'muted'">{{ summary.disputed || '—' }}</td>
          <td class="amount">{{ summary.total | number }}</td>
          <!-- The whole agreed season in one file: every criterion, every
               ruling with the name on it, the payment lines and the comment
               thread. It is what a production company gets when it asks how
               the amount was arrived at. -->
          <td class="export">
            <button mat-icon-button
                    [disabled]="!summary.approved || exporting !== null"
                    [matTooltip]="summary.approved
                      ? 'Download the ' + summary.approved + ' approved broadcasts as Excel — criteria, rulings, payment and the whole discussion'
                      : 'Nothing approved yet'"
                    (click)="exportProducer(summary, $event)">
              <mat-icon>{{ exporting === summary.channelId ? 'hourglass_top' : 'download' }}</mat-icon>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <p class="export-error" *ngIf="exportError">{{ exportError }}</p>
  </mat-card-content>
</mat-card>

<div class="toolbar">
  <mat-form-field appearance="outline">
    <mat-label>Producer</mat-label>
    <mat-select [(ngModel)]="producerFilter" (ngModelChange)="applyFilters()">
      <mat-option value="">All</mat-option>
      <mat-option *ngFor="let producer of producers" [value]="producer">{{ producer }}</mat-option>
    </mat-select>
  </mat-form-field>

  <mat-form-field appearance="outline">
    <mat-label>State</mat-label>
    <mat-select [(ngModel)]="stateFilter" (ngModelChange)="applyFilters()">
      <mat-option value="">All</mat-option>
      <mat-option *ngFor="let state of states" [value]="state">{{ stateLabels[state] }}</mat-option>
    </mat-select>
  </mat-form-field>

  <mat-form-field appearance="outline">
    <mat-label>Operated camera</mat-label>
    <mat-select [(ngModel)]="declarationFilter" (ngModelChange)="applyFilters()">
      <mat-option value="">All</mat-option>
      <mat-option *ngFor="let option of declarations" [value]="option.value">
        {{ option.label }}
      </mat-option>
    </mat-select>
  </mat-form-field>

  <span class="grow"></span>

  <button mat-flat-button color="primary"
          [disabled]="busy || !selectedReleasable.length"
          (click)="releaseSelected()">
    Release {{ selectedReleasable.length || '' }} to producers
  </button>
</div>

<table class="games">
  <thead>
    <tr>
      <th><mat-checkbox (change)="toggleAll($event.checked)"></mat-checkbox></th>
      <th>Date</th>
      <th>Broadcast</th>
      <th>Producer</th>
      <th>State</th>
      <th>Open items</th>
      <th>Operated camera</th>
      <th>Payment</th>
      <th></th>
    </tr>
  </thead>
  <tbody>
    <tr *ngFor="let row of visible">
      <td>
        <mat-checkbox [(ngModel)]="row.selected"
                      [disabled]="!releasable(row)"
                      [matTooltip]="releasable(row) ? '' : 'Only a fully decided, unreleased broadcast can go out'">
        </mat-checkbox>
      </td>
      <td>{{ row.game.date }}</td>
      <td>
        <a [routerLink]="['/evaluation/game', row.game.videoId]">{{ row.game.name }}</a>
        <span class="chip ct" *ngIf="row.game.ctSportPlus">ČT Sport+</span>
        <span class="chip drift" *ngIf="row.game.measurementDrift">drift</span>
      </td>
      <td>{{ row.game.producerName }}</td>
      <td><span class="chip state-{{ row.game.state }}">{{ stateLabels[row.game.state] }}</span></td>
      <td [ngClass]="row.pending ? 'warn' : 'muted'">{{ row.pending || '—' }}</td>
      <td class="camera">
        <span [ngClass]="row.declaration === 'undeclared' ? 'muted' : ''">{{ row.declared }}</span>
        <mat-icon class="ok" *ngIf="row.declaration === 'confirmed'"
                  [matTooltip]="'Confirmed by an admin'">check</mat-icon>
        <mat-icon class="disputed" *ngIf="row.declaration === 'disputed'"
                  [matTooltip]="row.game.declarations?.disputeNote || 'Disputed by an admin'"
                  >report_problem</mat-icon>
        <!-- Only a claim worth money gets an action. A none is finished when
             the producer makes it — there is nothing for an admin to agree to. -->
        <button mat-button class="confirm" *ngIf="row.declaration === 'toConfirm'"
                [disabled]="busy"
                matTooltip="Nobody has checked this claim yet — and it is already paid"
                (click)="confirm(row)">confirm</button>
      </td>
      <td class="amount" [ngClass]="row.pending ? 'muted' : ''">
        {{ row.total | number }}
        <span class="bad" *ngIf="row.blocked" matTooltip="An absolute condition is not met">·</span>
      </td>
      <td>
        <a mat-icon-button [href]="row.game.dashboardUrl" target="_blank" rel="noopener"
           matTooltip="StayLive dashboard">
          <mat-icon>open_in_new</mat-icon>
        </a>
      </td>
    </tr>
  </tbody>
</table>

<p class="muted" *ngIf="!visible.length">Nothing published for this season yet.</p>
